import { Row, Tag, Tooltip } from "antd";
import { Button } from "Components/General/Button";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ScrollableTable, TableColumnProps } from "Components/ScrollableTable/ScrollableTable";
import { FileTypeIcon } from "../Components/FileTypeIcon";
import { numberSort } from "utils/defaultSort";
import { dayjs } from "Config/dayjs";
import { FormatData } from "utils/FormatData";
import { documentsSearchStore, GroupedSearchResult } from "../Stores/DocumentsSearchStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fileDetailsStore } from "Components/FileDetails/FileDetailsStore";
import { observer } from "mobx-react";
import { DocumentsSearchbar } from "../Components/Search/DocumentsSearchbar";
import { DocumentsTableRowActions } from "../Components/DocumentsTableRowActions";
import { DocumentsBreadcrumbs } from "../Components/DocumentsBreadcrumbs";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { breadcrumbsStore } from "../Stores/BreadcrumbsStore";
import { EllipsisLeft } from "Components/General/EllipsisLeft";
import { getUrlParams } from "utils/getUrlParams";
import { FilePreview } from "Components/FileDetails/Components/FilePreview";
import { DirectDownload } from "../Components/DirectDownload";
import { DocumentsDnD } from "../Components/DocumentsDnD";
import { projectsDataStore } from "Stores";
import { CheckboxProps } from "antd/lib/checkbox";
import { withRouter, WithRouterProps } from "Components/General/WithRouter";
import classnames from "classnames";
import { useEffect, useState } from "react";
import { DocumentsActionsMenu } from "../Components/DocumentsActionsMenu";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { DeletedFilesSwitcher } from "../Components/DeletedFilesSwitcher";

interface DocumentsSearchProps extends WithRouterProps {}

export const DocumentsSearch = withRouter(
  observer((props: DocumentsSearchProps) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    // const [selectedFiles, setSelectedFiles] = useState<API_SearchResult[]>([]);
    // const [highlightedRowId, setHighlightedRowId] = useState<number>();

    useEffect(() => {
      if (breadcrumbsStore.state === "RAW") {
        (async () => await breadcrumbsStore.init())();
      }
    }, [breadcrumbsStore.state]);

    useEffect(() => {
      submitSearchFromQuery(props.location.search);
    }, [props.location.search]);

    useEffect(() => {
      const currentFile = documentsSearchStore.data?.find((item) => item.id === fileDetailsStore.data.id);
      if (currentFile) {
        currentFile.name = fileDetailsStore.data.name;
      }
    }, [fileDetailsStore.data.name]);

    useEffect(() => {
      return () => {
        documentsSearchStore.clear();
      };
    }, []);

    const handleExport = async () => {
      try {
        const files = documentsSearchStore.data.filter((item) => !item.isGroup);
        await documentsSearchStore.downloadExcel(files);
      } catch (error) {
        throw error;
      }
    };

    const handleSelectRows = ({ selectedRowKeys }) => {
      setSelectedFileIds(selectedRowKeys);
    };

    const resetSelection = () => {
      setSelectedFileIds([]);
    };

    const goToLinkedMail = (linkedMailId: number) => {
      if (linkedMailId) {
        props.navigate(`/project/${projectsDataStore.projectId}/email/linked/view/${linkedMailId}`);
      }
    };

    /**
     * Takes query string, parses it and submits search to files.
     */
    const submitSearchFromQuery = (queryString: string) => {
      // currently supports only ?command=userFiles&userId={id}&date={date} but there can be more in future
      const parsedQuery = getUrlParams(queryString);
      if (parsedQuery.command === "userFiles") {
        const { userId, date, fromViews } = parsedQuery;
        documentsSearchStore.setRadioValue("ALL");
        documentsSearchStore.submitSearch({ ownerId: +userId, uploadDate: date, fullRightsCheckSearch: !!fromViews });
      } else if (parsedQuery.command === "di") {
        documentsSearchStore.submitSearch({ id: +parsedQuery.id, mailSearch: true });
      }
    };

    const renderTypeIcon = ({ rowData: record }) => {
      if (record.isGroup) {
        return null;
      }
      if (record.preview) {
        return (
          <Tooltip overlay={<FilePreview fileId={record.id} hasPreview={record.preview} />}>
            <div style={{ position: "relative" }}>
              <FileTypeIcon
                state={record.state}
                size={record.size}
                relation={record.relations}
                type={record.type}
                malware={record.malware}
                onClick={() => goToLinkedMail(record.linkedMailId)}
                linkedMailId={record.linkedMailId}
              />
            </div>
          </Tooltip>
        );
      } else {
        return (
          <div style={{ position: "relative" }}>
            <FileTypeIcon
              state={record.state}
              relation={record.relations}
              size={record.size}
              type={record.type}
              malware={record.malware}
              onClick={() => goToLinkedMail(record.linkedMailId)}
              linkedMailId={record.linkedMailId}
            />
          </div>
        );
      }
    };

    const renderRowActions = ({ rowData }) => {
      if (rowData.isGroup) {
        return null;
      }
      return (
        <Row justify="end" align="middle">
          {!rowData.type?.startsWith("FO") && rowData.fileActions.download && (
            <DirectDownload module="DOCUMENTS" fileId={rowData.id} />
          )}
          <DocumentsTableRowActions isSearchActions data={rowData} currentFolderId={rowData.parentId}>
            <div className="docs-dropdown-trigger" onClick={(e) => e.stopPropagation()}>
              <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
            </div>
          </DocumentsTableRowActions>
        </Row>
      );
    };

    const columns: TableColumnProps<API_SearchResult>[] = [
      {
        dataKey: "id",
        width: 30,
        flexShrink: 0,
        cellRenderer: renderTypeIcon,
        disableSort: true,
      },
      {
        dataKey: "name",
        label: t("Name"),
        flexGrow: 1,
        width: 140,
      },
      {
        dataKey: "logicalPath",
        label: t("Directory"),
        flexGrow: 1,
        width: 200,
        cellRenderer: ({ cellData }) => (cellData ? <EllipsisLeft text={"/" + cellData} /> : null),
      },
      {
        dataKey: "description",
        label: t("Description"),
        width: 200,
      },
      {
        dataKey: "index",
        label: t("Index"),
        width: 120,
      },
      {
        dataKey: "created",
        label: t("Last change"),
        initialSortOrder: "DESC",
        width: 150,
        cellRenderer: ({ cellData }) => (cellData ? dayjs(cellData).format("lll") : null),
        sorter: (a, b) => numberSort(a.created, b.created),
      },
      {
        dataKey: "size",
        label: t("Size"),
        width: 80,
        cellRenderer: ({ cellData }) => (!isNaN(cellData) ? FormatData.formatBytes(cellData, 1) : null),
        sorter: (a, b) => numberSort(a.size, b.size),
      },
      {
        dataKey: "id",
        label: t("Actions"),
        disableSort: true,
        width: 80,
        flexShrink: 0,
        cellRenderer: renderRowActions,
      },
      {
        headerClassName: "invisible",
        dataKey: "fileState",
        width: 12,
        style: { padding: 0 },
        cellRenderer: ({ cellData }) => <div className={`status-marker marker-${cellData}`} />,
      },
    ];

    const searchResultsAmount = () => {
      return (
        <h3 className="text-center mb-2">
          {documentsSearchStore.total === 1
            ? `1 ${t("file found")}`
            : `${documentsSearchStore.total} ${t("files found")}`}
          {documentsSearchStore.numberOfMalwareFiles === 0 ? (
            ""
          ) : (
            <Tag color="orange" style={{ marginLeft: "10px" }}>{`${documentsSearchStore.numberOfMalwareFiles} ${t(
              "virus files contained!"
            )}`}</Tag>
          )}
        </h3>
      );
    };

    const getCheckboxProps = (rowData: GroupedSearchResult): CheckboxProps => {
      return rowData.isGroup && { disabled: true, checked: false, style: { display: "none" } };
    };

    const rowClassName = ({ isGroup, deleted, unread }: GroupedSearchResult) => {
      if (isGroup) {
        return "search-group-folder";
      }
      return classnames({ disabledCell: deleted, unread: unread });
    };

    const handleRowClick = ({ rowData }) => {
      const data = documentsSearchStore.data.slice();

      if (rowData.isGroup) {
        const filter = data.filter((item) => item?.parentId === rowData.id);

        const fileIds = filter.map((item) => item.id);
        const link = filter.find((file) => file.state === "EXTERN_SYMLINK" && file.linkId > 0);

        if (rowData.id > 0) {
          let url = `${window.location.origin}/${window.GLOBAL.API_BASE}/project/${projectsDataStore.projectId}/documents/docs/${rowData.id}/?file=${fileIds}`;
          if (link) {
            url += `&linkid=${link.linkId}`;
          }
          window.open(url, "_blank");
        }
      } else {
        let id: number;

        if (typeof rowData.id === "string" && rowData.startsWith("SYMLINK")) {
          id = +(rowData.id as string).split("/")[1].replace("SYMLINK_", "");
        } else {
          id = rowData.id;
        }
        if (rowData.state === "EXTERN_SYMLINK") {
          fileDetailsStore.initModal(rowData.parentId, id, {
            from: "documents",
            linkId: rowData.linkId,
          });
        } else {
          fileDetailsStore.initModal(rowData.parentId, id, {
            from: "documents",
          });
        }

        // this.setState({ highlightedRowId: rowData.id });
      }
    };

    const onShowDeletedChange = () => {
      documentsSearchStore.toggleShowDeleted();
    };

    return (
      <div className="documents-wrapper">
        <div className="documents-main">
          <Row justify="end" className="mb-md" style={{ flexWrap: "nowrap" }}>
            <DocumentsSearchbar disabled={!props.location.search} />
            <Button
              faIcon="file-excel"
              type="primary"
              onClick={handleExport}
              loading={documentsSearchStore.exportDownloadState === "LOADING"}
              style={{ marginLeft: "10px" }}
            >
              {t("Export")}
            </Button>
          </Row>
          <Row justify="space-between" align="middle" className="mb-sm">
            <ModalConsumer>
              {({ showModal: showModalFromConsumer }) => (
                <DocumentsActionsMenu
                  isSearch={true}
                  resetSelection={resetSelection}
                  files={documentsSearchStore.data.filter((file) => selectedFileIds.includes(file.uid))}
                  onAfterActionFinished={documentsSearchStore.refresh}
                  showModal={showModalFromConsumer}
                />
              )}
            </ModalConsumer>
            <div className="flex-center">
              <DeletedFilesSwitcher onChange={onShowDeletedChange} checked={documentsTableStore.showDeleted} />
            </div>
          </Row>
          <DocumentsBreadcrumbs goToFolder={documentsTableStore.goToFolder} config={breadcrumbsStore.data} />
          {documentsSearchStore.state === "DONE" && searchResultsAmount()}
          {/* DocumentsDnD is required for d'n'd to work... */}
          <DocumentsDnD disabled={true} uploadMode="DOCUMENTS">
            <ScrollableTable<GroupedSearchResult>
              stretchHeight={true}
              rowKey="uid"
              sortBy={documentsSearchStore.sortBy}
              sortDirection={documentsSearchStore.sortDirection}
              onSort={documentsSearchStore.sort}
              columns={columns}
              selectedRowKeys={selectedFileIds}
              onSelectRows={handleSelectRows}
              onRowClick={handleRowClick}
              loading={documentsSearchStore.state === "LOADING"}
              data={documentsSearchStore.data.slice()}
              // highlightedRowId={highlightedRowId}
              draggableRows={true}
              normalizeDropRowData={(rowData) => ({ type: rowData.type, id: rowData.id })}
              getDroppableFromRowData={() => false}
              getCheckboxProps={getCheckboxProps}
              rowClassName={rowClassName}
              onCheckAllPredicate={(record) => !record.isGroup}
              getDraggableFromRowData={(rowData) => rowData.fileActions?.collectionFolder || rowData.fileActions?.move}
              resizeableHeaders={"DOCUMENTS_SEARCH"}
              ignoreResizeableHeaders={["id", "size"]}
            />
          </DocumentsDnD>
        </div>
      </div>
    );
  })
);
